import React, { useState } from 'react';
import CodeEditor from './CodeEditor';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import Icon from '../../../components/AppIcon';

const APITester = () => {
  const [method, setMethod] = useState('GET');
  const [url, setUrl] = useState('https://jsonplaceholder.typicode.com/posts/1');
  const [headers, setHeaders] = useState('{\n  "Content-Type": "application/json",\n  "Authorization": "Bearer your-token-here"\n}');
  const [body, setBody] = useState('{\n  "title": "Sample Post",\n  "body": "This is a sample post body",\n  "userId": 1\n}');
  const [response, setResponse] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  const methodOptions = [
    { value: 'GET', label: 'GET' },
    { value: 'POST', label: 'POST' },
    { value: 'PUT', label: 'PUT' },
    { value: 'PATCH', label: 'PATCH' },
    { value: 'DELETE', label: 'DELETE' }
  ];

  const presetAPIs = [
    {
      name: 'Get User Posts',
      method: 'GET',
      url: 'https://jsonplaceholder.typicode.com/posts?userId=1',
      headers: '{\n  "Content-Type": "application/json"\n}',
      body: ''
    },
    {
      name: 'Create New Post',
      method: 'POST',
      url: 'https://jsonplaceholder.typicode.com/posts',
      headers: '{\n  "Content-Type": "application/json"\n}',
      body: '{\n  "title": "My New Post",\n  "body": "This is the content of my new post",\n  "userId": 1\n}'
    },
    {
      name: 'Get Weather Data',
      method: 'GET',
      url: 'https://api.openweathermap.org/data/2.5/weather?q=London&appid=demo',
      headers: '{\n  "Content-Type": "application/json"\n}',
      body: ''
    },
    {
      name: 'GitHub User Info',
      method: 'GET',
      url: 'https://api.github.com/users/octocat',
      headers: '{\n  "Accept": "application/vnd.github.v3+json"\n}',
      body: ''
    }
  ];

  const loadPreset = (preset) => {
    setMethod(preset?.method);
    setUrl(preset?.url);
    setHeaders(preset?.headers);
    setBody(preset?.body);
    setResponse('');
    setError(null);
  };

  const handleSendRequest = async () => {
    setIsLoading(true);
    setError(null);
    
    try {
      // Simulate API request
      await new Promise(resolve => setTimeout(resolve, 1500));
      
      // Mock response based on URL
      let mockResponse;
      if (url?.includes('jsonplaceholder.typicode.com/posts/1')) {
        mockResponse = {
          id: 1,
          title: "sunt aut facere repellat provident occaecati excepturi optio reprehenderit",
          body: "quia et suscipit\nsuscipit recusandae consequuntur expedita et cum\nreprehenderit molestiae ut ut quas totam\nnostrum rerum est autem sunt rem eveniet architecto",
          userId: 1
        };
      } else if (url?.includes('jsonplaceholder.typicode.com/posts?userId=1')) {
        mockResponse = [
          {
            id: 1,
            title: "sunt aut facere repellat provident occaecati excepturi optio reprehenderit",
            body: "quia et suscipit\nsuscipit recusandae consequuntur expedita et cum",
            userId: 1
          },
          {
            id: 2,
            title: "qui est esse",
            body: "est rerum tempore vitae\nsequi sint nihil reprehenderit dolor beatae ea dolores neque",
            userId: 1
          }
        ];
      } else if (url?.includes('github.com/users')) {
        mockResponse = {
          login: "octocat",
          id: 1,
          name: "The Octocat",
          company: "@github",
          blog: "https://github.blog",
          location: "San Francisco",
          bio: "There once was...",
          public_repos: 8,
          followers: 9001,
          following: 9
        };
      } else {
        mockResponse = {
          message: "API request successful",
          timestamp: new Date()?.toISOString(),
          method: method,
          url: url,
          status: 200
        };
      }

      const responseText = JSON.stringify(mockResponse, null, 2);
      setResponse(`HTTP/1.1 200 OK\nContent-Type: application/json\nDate: ${new Date()?.toUTCString()}\nContent-Length: ${responseText?.length}\n\n${responseText}`);
      
    } catch (err) {
      setError(`Request failed: ${err?.message}`);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Preset APIs */}
      <div>
        <h3 className="text-lg font-semibold mb-3 flex items-center space-x-2">
          <Icon name="Zap" size={20} className="text-accent" />
          <span>Quick Start Examples</span>
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
          {presetAPIs?.map((preset, index) => (
            <Button
              key={index}
              variant="outline"
              size="sm"
              onClick={() => loadPreset(preset)}
              className="justify-start h-auto p-3 text-left"
            >
              <div>
                <div className="font-medium text-sm">{preset?.name}</div>
                <div className="text-xs text-text-secondary mt-1">
                  <span className={`px-1.5 py-0.5 rounded text-xs font-mono ${
                    preset?.method === 'GET' ? 'bg-green-100 text-green-700' :
                    preset?.method === 'POST'? 'bg-blue-100 text-blue-700' : 'bg-orange-100 text-orange-700'
                  }`}>
                    {preset?.method}
                  </span>
                </div>
              </div>
            </Button>
          ))}
        </div>
      </div>
      {/* Request Configuration */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="space-y-4">
          <h3 className="text-lg font-semibold flex items-center space-x-2">
            <Icon name="Settings" size={20} className="text-accent" />
            <span>Request Configuration</span>
          </h3>
          
          {/* Method and URL */}
          <div className="flex space-x-2">
            <div className="w-24">
              <Select
                options={methodOptions}
                value={method}
                onChange={setMethod}
                placeholder="Method"
              />
            </div>
            <div className="flex-1">
              <Input
                type="url"
                value={url}
                onChange={(e) => setUrl(e?.target?.value)}
                placeholder="Enter API endpoint URL"
              />
            </div>
          </div>

          {/* Headers */}
          <div>
            <label className="block text-sm font-medium mb-2">Headers</label>
            <CodeEditor
              title="headers.json"
              language="json"
              initialCode={headers}
              onCodeChange={setHeaders}
              showOutput={false}
            />
          </div>

          {/* Body (for POST/PUT/PATCH) */}
          {['POST', 'PUT', 'PATCH']?.includes(method) && (
            <div>
              <label className="block text-sm font-medium mb-2">Request Body</label>
              <CodeEditor
                title="body.json"
                language="json"
                initialCode={body}
                onCodeChange={setBody}
                showOutput={false}
              />
            </div>
          )}

          {/* Send Button */}
          <Button
            variant="default"
            iconName="Send"
            iconPosition="left"
            loading={isLoading}
            onClick={handleSendRequest}
            className="w-full bg-accent hover:bg-hover text-accent-foreground"
          >
            {isLoading ? 'Sending Request...' : 'Send Request'}
          </Button>
        </div>

        {/* Response */}
        <div>
          <h3 className="text-lg font-semibold mb-3 flex items-center space-x-2">
            <Icon name="FileText" size={20} className="text-accent" />
            <span>Response</span>
          </h3>
          
          <div className="rounded-lg border border-border bg-slate-900 text-slate-100 min-h-96">
            <div className="flex items-center justify-between px-4 py-3 border-b border-slate-700 bg-slate-800">
              <span className="text-sm font-medium">Response</span>
              <div className="flex items-center space-x-2">
                {response && (
                  <span className="text-xs px-2 py-1 bg-green-600 text-white rounded">
                    200 OK
                  </span>
                )}
                {error && (
                  <span className="text-xs px-2 py-1 bg-red-600 text-white rounded">
                    Error
                  </span>
                )}
              </div>
            </div>
            
            <div className="p-4 font-mono text-sm">
              {isLoading ? (
                <div className="flex items-center space-x-2 text-slate-400">
                  <div className="animate-spin">
                    <Icon name="Loader2" size={16} />
                  </div>
                  <span>Sending request...</span>
                </div>
              ) : error ? (
                <div className="text-red-400">{error}</div>
              ) : response ? (
                <pre className="whitespace-pre-wrap">{response}</pre>
              ) : (
                <div className="text-slate-400 text-center py-8">
                  <Icon name="Send" size={32} className="mx-auto mb-2 opacity-50" />
                  <p>Configure your request and click "Send Request" to see the response</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
      {/* Tips */}
      <div className="bg-muted/30 rounded-lg p-4">
        <h4 className="font-medium mb-2 flex items-center space-x-2">
          <Icon name="Info" size={16} className="text-accent" />
          <span>API Testing Tips</span>
        </h4>
        <ul className="text-sm text-text-secondary space-y-1">
          <li>• Use the preset examples to quickly test common API patterns</li>
          <li>• Modify headers to test authentication and content types</li>
          <li>• Try different HTTP methods to understand RESTful API design</li>
          <li>• Check response status codes and headers for debugging</li>
          <li>• Use JSON formatting in request bodies for better readability</li>
        </ul>
      </div>
    </div>
  );
};

export default APITester;